import { mergeStatusDistribution } from "@/components/StatusDistribution/StatusDistribution";
import type { GroupIteration } from "@/model/board";
import type { RelativeCoordinate } from "@/model/coordinates";
import type { Art } from "@/model/session";
import { useArtStore } from "@/store/art";
import { useBoardsStore } from "@/store/boards";
import { useIterationStore } from "@/store/iteration";
import { useSessionStore } from "@/store/session";
import { useStickyTypeStore } from "@/store/stickyType";
import { useGlobalI18n } from "@/translations/i18n";

import { BasePlanningBoard } from "./BasePlanningBoard";
import { PlanningBoardLocation } from "./location/PlanningBoardLocation";

const { t } = useGlobalI18n();

export class SolutionBoard extends BasePlanningBoard<"solution"> {
  override get groups() {
    return [
      this.nonTeamGroup(t("programBoard.milestoneEvents")),
      ...useArtStore().arts.map(artGroup),
      this.nonTeamGroup(""),
    ];

    function artGroup(art: Art) {
      return {
        id: art.id,
        name: art.name,
        current: art.id === useArtStore().current.id,
        iterations: artIterations(art),
      };
    }

    function artIterations(art: Art): GroupIteration[] {
      const teamBoards = useBoardsStore().artTeamBoards(art.id);
      return useSessionStore().iterations.map((iter, i) => {
        const artIter = { ...iter, velocity: 0, load: 0, statusDistribution: new Map() };
        teamBoards.forEach((board) => {
          const boardIter = useIterationStore().iteration(board, i);
          artIter.velocity += boardIter.velocity;
          artIter.load += boardIter.load;
          mergeStatusDistribution(artIter.statusDistribution, boardIter.statusDistribution);
        });
        return artIter;
      });
    }
  }

  location(pos: RelativeCoordinate | number | string, top?: number) {
    return PlanningBoardLocation.ofArts(this.groups, pos, top);
  }

  override positionalCardProperties(pos: RelativeCoordinate) {
    const loc = this.location(pos);
    return { iterationId: loc.iterationId, artId: loc.groupId };
  }

  override get showPermanentLinks() {
    return true;
  }

  get possibleStickyTypes() {
    return useStickyTypeStore().nonFlexBoardStickyTypes("solution");
  }

  override get creatableStickyTypes() {
    // we can't select a team on the solution-planning board, hence we should not allow creating team board stickies (dependencies)
    // also we should not allow creating art backlog stickies on the solution-planning board, because we do not automatically create
    // the origin on the art backlog board (not implemented in the backend)
    return this.possibleStickyTypes.filter(
      (type) => type.origin !== "team" && type.origin !== "backlog",
    );
  }
}
